import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  FaUsers, FaUtensils, FaClipboardList, FaImages, FaBook,
  FaFileContract, FaMoneyBillWave, FaClock, FaEnvelope,
  FaSignOutAlt, FaPlus, FaEdit, FaTrash, FaCheck, FaWhatsapp,
  FaTimes, FaSave, FaStar, FaLeaf, FaChartBar, FaUpload, FaCamera,
  FaLink, FaRobot, FaCog, FaConciergeBell, FaHandshake, FaQuoteLeft,
  FaBell, FaSyncAlt, FaCheckCircle, FaExclamationCircle, FaInfoCircle,
  FaFilePdf, FaDownload, FaPrint, FaCalendarDay, FaCalendarAlt, FaCalendarWeek,
  FaImage,
  FaMoneyCheckAlt, FaWallet, FaUserTie, FaArrowDown, FaArrowUp, FaCoins, FaBullhorn
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../App';
import './ManagerDashboard.css';

const API = axios.create({
  baseURL: 'https://api.hospifylink.com'
});
API.interceptors.request.use(cfg => {
  const t = localStorage.getItem('lca_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const SECTIONS = [
  { key: 'overview',      label: 'Overview',      icon: <FaClipboardList /> },
  { key: 'payments',      label: 'Payments',      icon: <FaMoneyCheckAlt /> },
  { key: 'requests',      label: 'Requests',      icon: <FaClipboardList /> },
  { key: 'professionals', label: 'Professionals', icon: <FaUsers /> },
  { key: 'users',         label: 'Clients',       icon: <FaUsers /> },
  { key: 'recipes',       label: 'Recipes',       icon: <FaBook /> },
  { key: 'contracts',     label: 'Contracts',     icon: <FaFileContract /> },
  { key: 'reports',       label: 'Reports',       icon: <FaFilePdf /> },
  { key: 'hero',          label: 'Hero Slider',   icon: <FaImage /> },
  { key: 'team',          label: 'Team',          icon: <FaUsers /> },
  { key: 'partners',      label: 'Partners',      icon: <FaHandshake /> },
  { key: 'services',      label: 'Services',      icon: <FaConciergeBell /> },
  { key: 'gallery',       label: 'Gallery',       icon: <FaImages /> },
  { key: 'testimonials',  label: 'Testimonials',  icon: <FaQuoteLeft /> },
  { key: 'about',         label: 'About Page',    icon: <FaInfoCircle /> },
  { key: 'whychoose',     label: 'Why Choose Us', icon: <FaLeaf /> },
  { key: 'stats',         label: 'Stats',         icon: <FaChartBar /> },
  { key: 'updates', label: 'Updates', icon: <FaBullhorn /> },
  { key: 'footer',        label: 'Footer Settings', icon: <FaCog /> },
  { key: 'footerlinks',   label: 'Footer Links',  icon: <FaLink /> },
  { key: 'chatbot',       label: 'Chatbot Q&A',   icon: <FaRobot /> },
  { key: 'messages',      label: 'Messages',      icon: <FaEnvelope /> }
];

const IMAGE_SECTIONS = [
  'hero', 'professionals', 'gallery', 'recipes', 'team', 'services',
  'partners', 'testimonials', 'updates'
];

const SECTION_ENDPOINT_MAP = {
  hero: 'hero-all',
  requests: 'requests',
  professionals: 'professionals-all',
  users: 'users',
  services: 'services-all',
  gallery: 'gallery-all',
  recipes: 'recipes-all',
  contracts: 'contracts',
  team: 'team-all',
  partners: 'partners-all',
  testimonials: 'testimonials-all',
  about: 'about-all',
  whychoose: 'whychoose-all',
  stats: 'stats-all',
  footerLinks: 'footer/links-all',
  chatbot: 'chatbot/qa-all',
  messages: 'messages',
  updates: 'updates-all'
};

const writeEndpoint = (sectionKey) => {
  const read = SECTION_ENDPOINT_MAP[sectionKey] || sectionKey;
  return read.replace(/-all$/, '');
};

const AUTO_REFRESH_MS = 30000;

const STATUS_OPTIONS = ['pending', 'confirmed', 'approved', 'completed', 'rejected', 'cancelled'];

function InlineToast({ toast, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, toast.duration || 3000);
    return () => clearTimeout(timer);
  }, [toast, onClose]);

  const icons = {
    success: <FaCheckCircle />,
    error: <FaExclamationCircle />,
    info: <FaInfoCircle />
  };

  return (
    <div className={`inline-toast inline-toast-${toast.type}`}>
      <span className="inline-toast-icon">{icons[toast.type] || icons.success}</span>
      <span className="inline-toast-msg">{toast.message}</span>
      <button className="inline-toast-close" onClick={onClose}><FaTimes /></button>
      <div className="inline-toast-progress"
        style={{ animationDuration: `${toast.duration || 3000}ms` }} />
    </div>
  );
}

function ManagerDashboard() {
  const { manager, setManager } = useApp();
  const navigate = useNavigate();

  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, duration }]);
  };
  const removeToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  const [section, setSection] = useState('overview');
  const [stats, setStats] = useState({});
  const [data, setData] = useState({
    hero: [],
    requests: [], professionals: [], users: [], gallery: [], recipes: [],
    contracts: [], messages: [], team: [], whychoose: [], stats: [],
    services: [], footerLinks: [], chatbot: [],
    partners: [], testimonials: [], about: [],updates: []
  });
  const [footerSettings, setFooterSettings] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addData, setAddData] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [addImageFile, setAddImageFile] = useState(null);
  const [addImagePreview, setAddImagePreview] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const fileInputRef = useRef(null);
  const addFileInputRef = useRef(null);

  const [reportPeriod, setReportPeriod] = useState('daily');
  const [reportPreview, setReportPreview] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  /* ============ REJECTION MODAL STATE ============ */
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRequest, setRejectRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);

  /* ============ MOBILE NAV TOOLTIP STATE ============ */
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimerRef = useRef(null);


  /* ============ PAYMENTS STATE ============ */
const [payments, setPayments] = useState([]);
const [paymentFilter, setPaymentFilter] = useState('all'); // 'all' | 'client_payment' | 'professional_payout'
const [revenueSummary, setRevenueSummary] = useState({
  total_client_revenue: 0,
  total_professional_paid: 0,
  net_profit: 0,
  margin_percent: 0,
  total_pending: 0,
  client_payment_count: 0,
  professional_payout_count: 0
});
const [professionalsWithEarnings, setProfessionalsWithEarnings] = useState([]);
const [showPaymentModal, setShowPaymentModal] = useState(false);
const [paymentMode, setPaymentMode] = useState('client'); // 'client' | 'professional'
const [editingPaymentId, setEditingPaymentId] = useState(null);
const [paymentData, setPaymentData] = useState({
  amount: '',
  method: 'cash',
  reference: '',
  notes: '',
  request_id: '',
  contract_id: '',
  party_id: '',
  party_name: '',
  paid_at: new Date().toISOString().slice(0, 10)
});
  const showNavTooltip = (key) => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    setActiveTooltip(key);
    tooltipTimerRef.current = setTimeout(() => setActiveTooltip(null), 1800);
  };

  /* Cleanup tooltip timer on unmount */
  useEffect(() => () => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
  }, []);

  const loadStats = () =>
    API.get('/api/dashboard/stats').then(r => setStats(r.data)).catch(() => {});

    const loadPayments = () =>
  API.get('/api/payments').then(r => setPayments(r.data)).catch(() => {});

const loadRevenueSummary = () =>
  API.get('/api/payments/summary').then(r => setRevenueSummary(r.data)).catch(() => {});

const loadProfessionalsWithEarnings = () =>
  API.get('/api/professionals-with-earnings')
    .then(r => setProfessionalsWithEarnings(r.data))
    .catch(() => {});

  const loadAll = () => {
    const simpleEndpoints = [
      ['hero', 'hero-all'],
      ['requests', 'requests'],
      ['professionals', 'professionals-all'],
      ['users', 'users'],
      ['services', 'services-all'],
      ['gallery', 'gallery-all'],
      ['recipes', 'recipes-all'],
      ['contracts', 'contracts'],
      ['team', 'team-all'],
      ['whychoose', 'whychoose-all'],
      ['stats', 'stats-all'],
      ['partners', 'partners-all'],
      ['testimonials', 'testimonials-all'],
      ['updates', 'updates-all'],
      ['about', 'about-all']

    ];

    simpleEndpoints.forEach(([key, endpoint]) => {
      API.get(`/api/${endpoint}`)
        .then(r => setData(d => ({ ...d, [key]: r.data })))
        .catch(() => {});
    });

    API.get('/api/footer/links-all')
      .then(r => setData(d => ({ ...d, footerLinks: r.data })))
      .catch(() => {});
    API.get('/api/chatbot/qa-all')
      .then(r => setData(d => ({ ...d, chatbot: r.data })))
      .catch(() => {});
    API.get('/api/footer/settings')
      .then(r => setFooterSettings(r.data || {}))
      .catch(() => {});
    API.get('/api/messages')
      .then(r => setData(d => ({ ...d, messages: r.data })))
      .catch(() => {});

      API.get('/api/payments').then(r => setPayments(r.data)).catch(() => {});
API.get('/api/payments/summary').then(r => setRevenueSummary(r.data)).catch(() => {});
API.get('/api/professionals-with-earnings')
  .then(r => setProfessionalsWithEarnings(r.data))
  .catch(() => {});
  };

  

  const loadNotifications = () => {
    API.get('/api/notifications')
      .then(r => {
        setNotifications(r.data);
        setUnreadCount(r.data.filter(n => !n.is_read).length);
      })
      .catch(() => {});
  };

  const refreshAll = async (showFeedback = true) => {
    setRefreshing(true);
    try {
      await Promise.all([loadStats(), loadAll(), loadNotifications()]);
      setLastRefresh(new Date());
      if (showFeedback) showToast('Data refreshed successfully', 'success');
    } catch {
      if (showFeedback) showToast('Refresh failed', 'error');
    }
    setRefreshing(false);
  };

  useEffect(() => { refreshAll(false); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    const t = setInterval(() => refreshAll(false), AUTO_REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const markRead = async (id) => {
    try {
      await API.put(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(c => Math.max(0, c - 1));
      showToast('Marked as read', 'info');
    } catch { showToast('Failed to mark as read', 'error'); }
  };

  const markAllRead = async () => {
    try {
      await API.put('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
      showToast('All notifications marked as read', 'success');
    } catch { showToast('Failed to mark all as read', 'error'); }
  };

  const deleteNotification = async (id) => {
    try {
      await API.delete(`/api/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => {
        const removed = notifications.find(n => n.id === id);
        return removed && !removed.is_read ? Math.max(0, prev - 1) : prev;
      });
      showToast('Notification removed', 'info');
    } catch { showToast('Failed to delete notification', 'error'); }
  };

  const clearAllNotifs = async () => {
    if (!window.confirm('Clear all notifications?')) return;
    try {
      await API.delete('/api/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
      showToast('All notifications cleared', 'success');
    } catch { showToast('Failed to clear notifications', 'error'); }
  };

  const logout = () => {
    localStorage.removeItem('lca_token');
    setManager(null);
    showToast('Logged out successfully', 'info');
    setTimeout(() => navigate('/'), 500);
  };

  const startEdit = (row) => {
    setEditingRow(row.id);
    setEditingData({ ...row });
    setImageFile(null);
    setImagePreview(null);
    showToast('Editing…', 'info');
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditingData({});
    setImageFile(null);
    setImagePreview(null);
    showToast('Edit cancelled', 'info');
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select a valid image file', 'error'); return; }
    if (file.size > 10 * 1024 * 1024) { showToast('Image must be less than 10MB', 'error'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    showToast('Image loaded — click Save to apply', 'info');
  };

  const handleAddImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select a valid image file', 'error'); return; }
    if (file.size > 10 * 1024 * 1024) { showToast('Image must be less than 10MB', 'error'); return; }
    setAddImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAddImagePreview(reader.result);
    reader.readAsDataURL(file);
    showToast('Image selected', 'info');
  };

  const imageFieldFor = (sectionKey) => (sectionKey === 'partners' ? 'logo' : 'image');

  const saveEdit = async (sectionKey, id) => {
    setLoading(true);
    try {
      const endpoint = writeEndpoint(sectionKey);
      let payload, config = {};

      if (IMAGE_SECTIONS.includes(sectionKey)) {
        payload = new FormData();
        const imgField = imageFieldFor(sectionKey);

        Object.keys(editingData).forEach(key => {
          if (key !== 'image' && key !== 'logo'
              && editingData[key] !== null && editingData[key] !== undefined) {
            payload.append(key, editingData[key]);
          }
        });

        if (imageFile) payload.append(imgField, imageFile);
        else if (editingData[imgField]) payload.append(imgField, editingData[imgField]);

        config.headers = { 'Content-Type': 'multipart/form-data' };
      } else {
        payload = editingData;
      }

      await API.put(`/api/${endpoint}/${id}`, payload, config);
      setEditingRow(null);
      setEditingData({});
      setImageFile(null);
      setImagePreview(null);
      await refreshAll(false);
      showToast('Updated successfully ✅', 'success');
    } catch (err) {
      showToast('Save failed: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const remove = async (sectionKey, id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const endpoint = writeEndpoint(sectionKey);
      await API.delete(`/api/${endpoint}/${id}`);
      await refreshAll(false);
      showToast('Deleted successfully 🗑️', 'success');
    } catch { showToast('Delete failed', 'error'); }
  };

  const openAdd = () => {
    setAddData({ is_active: 1 });
    setAddImageFile(null);
    setAddImagePreview(null);
    setShowAddModal(true);
    showToast(`Opening Add ${section} form…`, 'info');
  };

  const saveAdd = async (sectionKey) => {
    setLoading(true);
    try {
      const endpoint = writeEndpoint(sectionKey);
      let payload, config = {};

      if (IMAGE_SECTIONS.includes(sectionKey)) {
        payload = new FormData();
        const imgField = imageFieldFor(sectionKey);

        Object.keys(addData).forEach(key => {
          if (key !== 'image' && key !== 'logo'
              && addData[key] !== null && addData[key] !== undefined) {
            payload.append(key, addData[key]);
          }
        });

        if (addImageFile) payload.append(imgField, addImageFile);
        config.headers = { 'Content-Type': 'multipart/form-data' };
      } else {
        payload = addData;
      }

      await API.post(`/api/${endpoint}`, payload, config);
      setShowAddModal(false);
      setAddData({});
      setAddImageFile(null);
      setAddImagePreview(null);
      await refreshAll(false);
      showToast('Added successfully ✅', 'success');
    } catch (err) {
      showToast('Add failed: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };

  const toggleActive = async (sectionKey, row) => {
    try {
      const endpoint = writeEndpoint(sectionKey);
      const newVal = row.is_active ? 0 : 1;

      const payload = { ...row, is_active: newVal };
      const imgField = imageFieldFor(sectionKey);
      if (row[imgField]) payload[imgField] = row[imgField];

      await API.put(`/api/${endpoint}/${row.id}`, payload);
      setData(d => ({
        ...d,
        [sectionKey]: d[sectionKey].map(r =>
          r.id === row.id ? { ...r, is_active: newVal } : r
        )
      }));
      showToast(newVal ? 'Activated ✅' : 'Deactivated ⛔', newVal ? 'success' : 'info');
    } catch (err) {
      showToast('Failed to toggle: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const saveFooterSettings = async () => {
    setLoading(true);
    try {
      await API.put('/api/footer/settings', footerSettings);
      showToast('Footer settings saved ✅', 'success');
      await refreshAll(false);
    } catch (err) {
      showToast('Failed: ' + (err.response?.data?.error || err.message), 'error');
    }
    setLoading(false);
  };


  /* ============ PAYMENT HANDLERS (MANUAL) ============ */
const openClientPayment = () => {
  setPaymentMode('client');
  setEditingPaymentId(null);
  setPaymentData({
    amount: '', method: 'cash', reference: '', notes: '',
    request_id: '', contract_id: '', party_id: '', party_name: '',
    paid_at: new Date().toISOString().slice(0, 10)
  });
  setShowPaymentModal(true);
};

const openProfessionalPayout = () => {
  setPaymentMode('professional');
  setEditingPaymentId(null);
  setPaymentData({
    amount: '', method: 'cash', reference: '', notes: '',
    request_id: '', contract_id: '', party_id: '', party_name: '',
    paid_at: new Date().toISOString().slice(0, 10)
  });
  setShowPaymentModal(true);
};

const editPayment = (p) => {
  setEditingPaymentId(p.id);
  setPaymentMode(p.payment_type === 'client_payment' ? 'client' : 'professional');
  setPaymentData({
    amount: p.amount,
    method: p.method || 'cash',
    reference: p.reference || '',
    notes: p.notes || '',
    request_id: p.request_id || '',
    contract_id: p.contract_id || '',
    party_id: p.party_id || '',
    party_name: p.party_name || '',
    paid_at: p.paid_at ? new Date(p.paid_at).toISOString().slice(0, 10) : ''
  });
  setShowPaymentModal(true);
};

const savePayment = async () => {
  if (!paymentData.amount || Number(paymentData.amount) <= 0) {
    showToast('Please enter a valid amount', 'error');
    return;
  }
  const payload = {
    payment_type: paymentMode === 'client' ? 'client_payment' : 'professional_payout',
    party_type: paymentMode === 'client' ? 'client' : 'professional',
    party_id: paymentData.party_id || null,
    party_name: paymentData.party_name || '',
    amount: Number(paymentData.amount),
    currency: 'RWF',
    method: paymentData.method,
    reference: paymentData.reference,
    notes: paymentData.notes,
    request_id: paymentData.request_id || null,
    contract_id: paymentData.contract_id || null,
    status: 'completed',
    paid_at: paymentData.paid_at
  };

  setLoading(true);
  try {
    if (editingPaymentId) {
      await API.put(`/api/payments/${editingPaymentId}`, payload);
      showToast('Payment updated ✅', 'success');
    } else {
      await API.post('/api/payments', payload);
      showToast(
        paymentMode === 'client'
          ? 'Client payment recorded 💰'
          : 'Professional payout recorded 💸',
        'success'
      );
    }
    setShowPaymentModal(false);
    await Promise.all([
      loadPayments(),
      loadRevenueSummary(),
      loadProfessionalsWithEarnings(),
      loadStats()
    ]);
  } catch (err) {
    showToast('Save failed: ' + (err.response?.data?.error || err.message), 'error');
  }
  setLoading(false);
};

const removePayment = async (p) => {
  if (!window.confirm('Delete this payment record?')) return;
  try {
    await API.delete(`/api/payments/${p.id}`);
    await Promise.all([
      loadPayments(),
      loadRevenueSummary(),
      loadProfessionalsWithEarnings(),
      loadStats()
    ]);
    showToast('Payment deleted 🗑️', 'success');
  } catch {
    showToast('Delete failed', 'error');
  }
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  /* ============ REQUEST STATUS CHANGE ============ */
  const changeRequestStatus = async (row, newStatus) => {
    if (newStatus === 'rejected') {
      setRejectRequest(row);
      setRejectionReason('');
      setShowRejectModal(true);
      return;
    }

    try {
      await API.put(`/api/requests/${row.id}`, {
        status: newStatus,
        notes: row.notes,
        number_of_professionals: row.number_of_professionals
      });
      setData(d => ({
        ...d,
        requests: d.requests.map(r =>
          r.id === row.id ? { ...r, status: newStatus } : r
        )
      }));
      showToast(`Status changed to ${newStatus} — client notified 📧`, 'success');
    } catch (err) {
      showToast('Failed to update status: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      showToast('Please enter a reason for rejection', 'error');
      return;
    }
    setRejectLoading(true);
    try {
      await API.put(`/api/requests/${rejectRequest.id}`, {
        status: 'rejected',
        notes: rejectRequest.notes,
        number_of_professionals: rejectRequest.number_of_professionals,
        rejection_reason: rejectionReason
      });
      setData(d => ({
        ...d,
        requests: d.requests.map(r =>
          r.id === rejectRequest.id ? { ...r, status: 'rejected' } : r
        )
      }));
      showToast('Request rejected — client notified with reason 📧', 'success');
      setShowRejectModal(false);
      setRejectRequest(null);
      setRejectionReason('');
    } catch (err) {
      showToast('Failed: ' + (err.response?.data?.error || err.message), 'error');
    }
    setRejectLoading(false);
  };

  /* ============ CONTRACTS + PDF ============ */
  const signContract = async (c) => {
    try {
      await API.put(`/api/contracts/${c.id}`, { ...c, signed: c.signed ? 0 : 1 });
      await refreshAll(false);
      showToast(c.signed ? 'Contract marked unsigned' : 'Contract signed ✅', 'success');
    } catch { showToast('Failed to update contract', 'error'); }
  };

  const fetchContractPDF = async (c) => {
    const res = await API.get(`/api/contracts/${c.id}/pdf`, { responseType: 'arraybuffer' });
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const downloadContractPDF = async (c, silent = false) => {
  try {
    if (!silent) showToast('Preparing PDF…', 'info');
    const blob = await fetchContractPDF(c);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Hospify-Link-Contract-${c.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    if (!silent) showToast('PDF downloaded ✅', 'success');
    return true;
  } catch (err) {
    if (!silent) showToast('Failed to generate PDF', 'error');
    return false;
  }
};

  const printContractPDF = async (c) => {
    try {
      showToast('Preparing print…', 'info');
      const blob = await fetchContractPDF(c);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) win.onload = () => { try { win.print(); } catch {} };
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      showToast('Print window opened 🖨️', 'info');
    } catch { showToast('Failed to open print', 'error'); }
  };

 const shareContractWhatsApp = async (c) => {
  try {
    // 1. Auto-download the PDF so the user can attach it in WhatsApp
    showToast('Preparing PDF for WhatsApp…', 'info');
    await downloadContractPDF(c, true); // silent mode — no extra toast

    // 2. Build a professional WhatsApp message
    const ref = `#HL-${String(c.id).padStart(5, '0')}`;
    const amount = Number(c.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
    const text =
      `*HOSPIFY LINK — CONTRACT ${ref}*\n\n` +
      `Hello ${c.party_name || 'there'},\n\n` +
      `Please find your contract details below. The official PDF has been downloaded to your device — kindly attach it in this chat.\n\n` +
      `*Service:* ${c.contract_type || '-'}\n` +
      `*Amount:* ${amount} RWF\n` +
      `*Period:* ${c.start_date || '-'} → ${c.end_date || '-'}\n` +
      `*Status:* ${c.signed ? 'Signed ✅' : 'Pending Signature ⏳'}\n\n` +
      `For any questions, contact us at +250 789 951 922.\n\n` +
      `— Hospify Link Ltd.`;

    // 3. Open WhatsApp with pre-filled message
    const phone = (c.party_phone || '').replace(/\D/g, '');
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;

    window.open(url, '_blank');

    showToast('WhatsApp opened — attach the downloaded PDF to send 📎', 'success', 5000);
  } catch (err) {
    showToast('Failed to open WhatsApp: ' + err.message, 'error');
  }
};

 const shareContractEmail = async (c) => {
  // Ask the manager to confirm / edit the recipient email
  const defaultTo = c.party_email || '';
  const recipient = window.prompt(
    'Send contract by email to:',
    defaultTo
  );

  if (!recipient || !recipient.trim()) {
    showToast('Email cancelled — no recipient', 'info');
    return;
  }

  // Basic email validation
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRe.test(recipient.trim())) {
    showToast('Invalid email address', 'error');
    return;
  }

  try {
    showToast(`Sending contract to ${recipient.trim()}…`, 'info');

    const res = await API.post(`/api/contracts/${c.id}/send-email`, {
      to: recipient.trim()
    });

    if (res.data?.success) {
      showToast(`Contract emailed to ${res.data.sentTo} with PDF attached ✅`, 'success', 5000);
    } else {
      showToast('Email send failed', 'error');
    }
  } catch (err) {
    showToast(
      'Email failed: ' + (err.response?.data?.error || err.message),
      'error',
      5000
    );
  }
};

  /* ============ REPORTS ============ */
  const loadReportPreview = async (period) => {
    setReportLoading(true);
    try {
      const r = await API.get(`/api/reports/preview?period=${period}`);
      setReportPreview(r.data);
    } catch { showToast('Failed to load report preview', 'error'); }
    setReportLoading(false);
  };

  const generateReportPDF = async (period, action = 'download') => {
    setReportLoading(true);
    try {
      showToast(`Generating ${period} report…`, 'info');
      const res = await API.get(`/api/reports?period=${period}`, { responseType: 'arraybuffer' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      if (action === 'download') {
        const a = document.createElement('a');
        a.href = url;
        a.download = `HospifyLink-Report-${period}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        showToast('Report downloaded ✅ Also saved in your Documents/Hospify Link Reports', 'success');
      } else if (action === 'print') {
        const win = window.open(url, '_blank');
        if (win) win.onload = () => { try { win.print(); } catch {} };
        showToast('Print window opened 🖨️', 'info');
      }

      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      showToast('Failed to generate report', 'error');
    }
    setReportLoading(false);
  };

  /* ============ CELL RENDERERS ============ */
  const renderEditableCell = (row, field, type = 'text', options = null) => {
    const isEditing = editingRow === row.id;
    if (!isEditing) return <span>{row[field] ?? '-'}</span>;

    if (type === 'select' && options) {
      return (
        <select value={editingData[field] || ''}
          onChange={e => setEditingData({ ...editingData, [field]: e.target.value })}
          className="excel-input">
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (type === 'textarea') {
      return (
        <textarea value={editingData[field] || ''}
          onChange={e => setEditingData({ ...editingData, [field]: e.target.value })}
          className="excel-input" rows="2" />
      );
    }
    return (
      <input type={type} value={editingData[field] || ''}
        onChange={e => setEditingData({ ...editingData, [field]: e.target.value })}
        className="excel-input" />
    );
  };

  const renderImageCell = (row, field = 'image') => {
    const isEditing = editingRow === row.id;
    const displayImage = isEditing && imagePreview ? imagePreview : row[field];
    return (
      <div className="excel-image-cell">
        <img src={displayImage || 'https://via.placeholder.com/80'} alt="" className="excel-thumb" />
        {isEditing && (
          <div className="excel-image-upload">
            <input type="file" accept="image/*" ref={fileInputRef}
              onChange={handleImageSelect} style={{ display: 'none' }} />
            <button type="button" className="excel-image-btn"
              onClick={() => fileInputRef.current?.click()} title="Upload new image">
              <FaCamera />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderActiveCell = (sectionKey, row) => {
    if (editingRow === row.id) return <span>—</span>;
    const isActive = row.is_active === 1 || row.is_active === true;
    return (
      <button
        className={`active-toggle ${isActive ? 'active-on' : 'active-off'}`}
        onClick={() => toggleActive(sectionKey, row)}
        title={isActive ? 'Deactivate (hide from public)' : 'Activate (show on public)'}
      >
        <span className="active-toggle-track">
          <span className="active-toggle-thumb" />
        </span>
        <span className="active-toggle-label">{isActive ? 'Active' : 'Inactive'}</span>
      </button>
    );
  };

  const renderActions = (sectionKey, row) => {
    const isEditing = editingRow === row.id;
    if (isEditing) {
      return (
        <div className="excel-actions">
          <button className="excel-btn excel-save"
            onClick={() => saveEdit(sectionKey, row.id)}
            disabled={loading} title="Save"><FaSave /></button>
          <button className="excel-btn excel-cancel" onClick={cancelEdit} title="Cancel"><FaTimes /></button>
        </div>
      );
    }
    return (
      <div className="excel-actions">
        <button className="excel-btn excel-edit" onClick={() => startEdit(row)} title="Edit"><FaEdit /></button>
        <button className="excel-btn excel-delete"
          onClick={() => remove(sectionKey, row.id)} title="Delete"><FaTrash /></button>
      </div>
    );
  };

  const renderAddImageField = (sectionKey) => {
    if (!IMAGE_SECTIONS.includes(sectionKey)) return null;
    const label = sectionKey === 'partners' ? 'Logo' : 'Image';
    return (
      <div className="add-image-field">
        <label className="add-image-label">{label}</label>
        <div className="add-image-preview-box">
          {addImagePreview ? (
            <img src={addImagePreview} alt="preview" className="add-image-preview" />
          ) : (
            <div className="add-image-placeholder">
              <FaImages /><span>No {label.toLowerCase()} selected</span>
            </div>
          )}
        </div>
        <input type="file" accept="image/*" ref={addFileInputRef}
          onChange={handleAddImageSelect} style={{ display: 'none' }} />
        <button type="button" className="add-image-btn"
          onClick={() => addFileInputRef.current?.click()}>
          <FaUpload /> Choose {label}
        </button>
        {addImageFile && <p className="add-image-name">Selected: {addImageFile.name}</p>}
      </div>
    );
  };

  const renderAddModal = (sectionKey, fields) => {
    if (!showAddModal) return null;
    return (
      <div className="dashboard-editor-overlay" onClick={() => setShowAddModal(false)}>
        <div className="dashboard-editor" onClick={e => e.stopPropagation()}>
          <h3>Add New {sectionKey}</h3>
          {renderAddImageField(sectionKey)}
          {fields.map(f => {
            if (f.type === 'textarea') {
              return (
                <textarea key={f.name}
                  placeholder={f.placeholder || f.name}
                  value={addData[f.name] || ''}
                  onChange={e => setAddData({ ...addData, [f.name]: e.target.value })}
                  rows="3" />
              );
            }
            if (f.type === 'select') {
              return (
                <select key={f.name}
                  value={addData[f.name] || ''}
                  onChange={e => setAddData({ ...addData, [f.name]: e.target.value })}>
                  <option value="">-- {f.placeholder} --</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              );
            }
            return (
              <input key={f.name} type={f.type || 'text'}
                placeholder={f.placeholder || f.name}
                value={addData[f.name] || ''}
                onChange={e => setAddData({ ...addData, [f.name]: e.target.value })} />
            );
          })}
          <div className="dashboard-editor-actions">
            <button className="lca-btn-primary" disabled={loading} onClick={() => saveAdd(sectionKey)}>
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button className="dashboard-editor-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  /* ============ STATUS SELECT RENDERER ============ */
  const renderStatusCell = (row) => {
    if (editingRow === row.id) {
      return (
        <select value={editingData.status || 'pending'}
          onChange={e => setEditingData({ ...editingData, status: e.target.value })}
          className="excel-input">
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }

    const statusColors = {
      pending: { bg: '#fff3cd', color: '#856404' },
      confirmed: { bg: '#d1ecf1', color: '#0c5460' },
      approved: { bg: '#d4edda', color: '#155724' },
      completed: { bg: '#cce5ff', color: '#004085' },
      cancelled: { bg: '#f8d7da', color: '#721c24' },
      rejected: { bg: '#f8d7da', color: '#721c24' }
    };
    const c = statusColors[row.status] || statusColors.pending;

    return (
      <div className="request-status-cell">
        <span style={{
          display: 'inline-block',
          padding: '5px 12px',
          background: c.bg,
          color: c.color,
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginRight: '8px'
        }}>{row.status}</span>
        <select
          value=""
          onChange={e => {
            if (e.target.value) changeRequestStatus(row, e.target.value);
          }}
          className="request-status-select"
          title="Change status (sends email to client)"
        >
          <option value="">Change…</option>
          {STATUS_OPTIONS.filter(s => s !== row.status).map(s => (
            <option key={s} value={s}>
              {s === 'rejected' ? '❌ Reject (enter reason)' : `→ ${s}`}
            </option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <div className="dashboard-root">
      <div className="inline-toast-container">
        {toasts.map(t => (
          <InlineToast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
        ))}
      </div>

      <aside className="dashboard-sidebar">
        <div className="dashboard-sidebar-brand">Hospify Link</div>
        <div className="dashboard-sidebar-user">
          <div className="dashboard-avatar">{manager?.name?.[0] || 'M'}</div>
          <div>
            <p>{manager?.name}</p>
            <span>{manager?.email}</span>
          </div>
        </div>
        <nav className="dashboard-nav">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              className={
                (section === s.key ? 'dashboard-nav-active ' : '') +
                (activeTooltip === s.key ? 'nav-tooltip-show' : '')
              }
              onClick={() => {
                showNavTooltip(s.key);
                setSection(s.key);
                if (s.key === 'reports') loadReportPreview(reportPeriod);
              }}
              onMouseEnter={() => setActiveTooltip(s.key)}
              onMouseLeave={() => setActiveTooltip(null)}
              onTouchStart={() => showNavTooltip(s.key)}
              aria-label={s.label}
            >
              {s.icon} <span>{s.label}</span>
              {/* Tooltip — visible on mobile when the icon is tapped */}
              <span className="nav-tooltip" role="tooltip">{s.label}</span>
            </button>
          ))}
        </nav>
        <button className="dashboard-logout" onClick={logout}>
          <FaSignOutAlt /> Logout
        </button>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-topbar">
          <h2>{SECTIONS.find(s => s.key === section)?.label}</h2>

          <div className="dashboard-topbar-actions">

            <button className={`dash-refresh-btn ${refreshing ? 'spinning' : ''}`}
              onClick={() => refreshAll(true)}
              title="Refresh now (auto every 30s)"
              disabled={refreshing}>
              <FaSyncAlt />
            </button>

            <div className="dash-notif-wrap">
              <button className="dash-notif-btn" onClick={() => setShowNotifPanel(v => !v)}
                title="Notifications">
                <FaBell />
                {unreadCount > 0 && (
                  <span className="dash-notif-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifPanel && (
                <>
                  <div className="dash-notif-backdrop" onClick={() => setShowNotifPanel(false)} />
                  <div className="dash-notif-panel">
                    <div className="dash-notif-head">
                      <span>Notifications {unreadCount > 0 && `(${unreadCount} new)`}</span>
                      <div className="dash-notif-head-actions">
                        <button onClick={markAllRead} title="Mark all as read">✓ All</button>
                        <button onClick={clearAllNotifs} title="Clear all">🗑 Clear</button>
                      </div>
                    </div>
                    <div className="dash-notif-list">
                      {notifications.length === 0 && (
                        <p className="dash-notif-empty">No notifications yet.</p>
                      )}
                      {notifications.map(n => (
                        <div key={n.id} className={`dash-notif-item ${!n.is_read ? 'unread' : ''}`}>
                          <div className="dash-notif-item-body">
                            <div className="dash-notif-item-title">
                              {!n.is_read && <span className="dash-notif-dot" />}
                              {n.title}
                            </div>
                            <div className="dash-notif-item-msg">{n.message}</div>
                            <div className="dash-notif-item-time">
                              {new Date(n.created_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="dash-notif-item-actions">
                            {!n.is_read && (
                              <button onClick={() => markRead(n.id)} title="Mark as read"><FaCheck /></button>
                            )}
                            <button onClick={() => deleteNotification(n.id)} title="Delete" className="del"><FaTrash /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="dash-notif-foot">
                      Auto-refresh every 30s • Last: {lastRefresh.toLocaleTimeString()}
                    </div>
                  </div>
                </>
              )}
            </div>
                                  {/* ===== Logout icon button ===== */}
            <button
              className="dash-logout-btn"
              onClick={logout}
              title="Logout"
              aria-label="Logout"
            >
              <FaSignOutAlt />
            </button>
          </div>
        </header>

        {/* ================= OVERVIEW ================= */}
        {section === 'overview' && (
          <div>
            <div className="dashboard-stats-grid">
            {[
  { label: 'Clients',        value: stats.users,         icon: <FaUsers />,          color: '#0074D9' },
  { label: 'Professionals',  value: stats.professionals, icon: <FaUtensils />,       color: '#0a5c36' },
  { label: 'Requests',       value: stats.requests,      icon: <FaClipboardList />,  color: '#f5a623' },
  { label: 'Pending',        value: stats.pending,       icon: <FaClock />,          color: '#d0021b' },
  { label: 'Client Revenue (RWF)', value: stats.clientRevenue,        icon: <FaArrowDown />,    color: '#0a5c36' },
  { label: 'Paid to Pros (RWF)',   value: stats.professionalPayouts,  icon: <FaArrowUp />,      color: '#c2620f' },
  { label: 'Your Earnings (RWF)',  value: stats.netProfit,            icon: <FaWallet />,       color: '#27ae60' },
  { label: 'Contracts',      value: stats.contracts,     icon: <FaFileContract />,   color: '#e67e22' }
].map((s, i) => (
  <div key={i} className="dashboard-stat-card fade-up"
    style={{ animationDelay: `${i * 0.05}s`, borderTopColor: s.color }}>
    <div className="dashboard-stat-icon" style={{ color: s.color }}>{s.icon}</div>
    <h3>{s.value ?? 0}</h3>
    <p>{s.label}</p>
  </div>
))}
            </div>
            <div className="dashboard-recent">
              <h3>Recent Requests</h3>
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Request #</th><th>Client</th><th>Service</th>
                    <th>Event</th><th>Staff</th><th>Date</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.slice(0, 8).map(r => (
                    <tr key={r.id}>
                      <td>{r.request_number}</td>
                      <td>{r.full_name}</td>
                      <td>{r.service_type}</td>
                      <td>{r.event_type}</td>
                      <td>{r.number_of_professionals || 1}</td>
                      <td>{r.event_date ? new Date(r.event_date).toLocaleDateString() : '-'}</td>
                      <td><span className={`dashboard-status status-${r.status}`}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= HERO SLIDER ================= */}
        {section === 'hero' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Hero Slider Slides</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Slide</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Image</th><th>Title</th><th>Subtitle</th>
                    <th>Primary Btn</th><th>Secondary Btn</th><th>Link</th>
                    <th>Order</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.hero.map(h => (
                    <tr key={h.id} className={h.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(h, 'image')}</td>
                      <td>{renderEditableCell(h, 'title')}</td>
                      <td>{renderEditableCell(h, 'subtitle')}</td>
                      <td>{renderEditableCell(h, 'primary_btn_label')}</td>
                      <td>{renderEditableCell(h, 'secondary_btn_label')}</td>
                      <td>{renderEditableCell(h, 'secondary_btn_link')}</td>
                      <td>{renderEditableCell(h, 'sort_order', 'number')}</td>
                      <td>{renderActiveCell('hero', h)}</td>
                      <td>{renderActions('hero', h)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= UPDATES ================= */}
{section === 'updates' && (
  <div className="dashboard-section">
    <div className="dashboard-section-head">
      <h3>Updates (Latest & Upcoming)</h3>
      <button className="lca-btn-primary" onClick={openAdd}>
        <FaPlus /> Add Update
      </button>
    </div>
    <div className="excel-table-wrap">
      <table className="excel-table">
        <thead>
          <tr>
            <th>Image</th>
            <th>Title</th>
            <th>Short Description</th>
            <th>Type</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.updates.map(u => (
            <tr key={u.id} className={u.is_active ? '' : 'row-inactive'}>
              <td>{renderImageCell(u, 'image')}</td>
              <td>{renderEditableCell(u, 'title')}</td>
              <td>{renderEditableCell(u, 'description', 'textarea')}</td>
              <td>
                {renderEditableCell(u, 'type', 'select', ['latest', 'upcoming'])}
              </td>
              <td>{renderActiveCell('updates', u)}</td>
              <td>{renderActions('updates', u)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Extended fields (long description) when a row is in edit mode */}
    {editingRow && data.updates.find(u => u.id === editingRow) && (
      <div className="services-extended-editor">
        <h4>Extended fields for: {editingData.title}</h4>
        <div className="services-extended-grid">
          <label>
            <span>Long Description (shown in modal)</span>
            <textarea
              rows="5"
              value={editingData.long_description || ''}
              onChange={e =>
                setEditingData({ ...editingData, long_description: e.target.value })
              }
            />
          </label>
        </div>
      </div>
    )}
  </div>
)}
        {/* ================= PAYMENTS ================= */}
{section === 'payments' && (
  <div className="dashboard-section">
    <div className="dashboard-section-head">
      <h3>Payments & Revenue</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="lca-btn-primary" onClick={openClientPayment}>
          <FaArrowDown /> Record Client Payment
        </button>
        <button className="lca-btn-primary" onClick={openProfessionalPayout}
          style={{ background: 'linear-gradient(135deg,#c2620f,#e67e22)' }}>
          <FaArrowUp /> Pay a Professional
        </button>
      </div>
    </div>

    {/* Revenue summary cards */}
    <div className="revenue-summary-grid">
      <div className="revenue-card revenue-card-client">
        <div className="revenue-card-icon"><FaCoins /></div>
        <div>
          <p className="revenue-card-label">Total Client Revenue</p>
          <h3 className="revenue-card-value">{fmtMoney(revenueSummary.total_client_revenue)} <span>RWF</span></h3>
          <p className="revenue-card-sub">{revenueSummary.client_payment_count} payments received</p>
        </div>
      </div>

      <div className="revenue-card revenue-card-professional">
        <div className="revenue-card-icon"><FaUserTie /></div>
        <div>
          <p className="revenue-card-label">Paid to Professionals</p>
          <h3 className="revenue-card-value">{fmtMoney(revenueSummary.total_professional_paid)} <span>RWF</span></h3>
          <p className="revenue-card-sub">{revenueSummary.professional_payout_count} payouts made</p>
        </div>
      </div>

      <div className="revenue-card revenue-card-profit">
        <div className="revenue-card-icon"><FaWallet /></div>
        <div>
          <p className="revenue-card-label">Your Net Earnings</p>
          <h3 className="revenue-card-value">{fmtMoney(revenueSummary.net_profit)} <span>RWF</span></h3>
          <p className="revenue-card-sub">Profit margin: {revenueSummary.margin_percent}%</p>
        </div>
      </div>
    </div>

    {/* Filter tabs */}
    <div className="payments-filter-bar">
      <button
        className={`payments-filter-btn ${paymentFilter === 'all' ? 'active' : ''}`}
        onClick={() => setPaymentFilter('all')}>
        All ({payments.length})
      </button>
      <button
        className={`payments-filter-btn ${paymentFilter === 'client_payment' ? 'active' : ''}`}
        onClick={() => setPaymentFilter('client_payment')}>
        <FaArrowDown /> Client Payments
      </button>
      <button
        className={`payments-filter-btn ${paymentFilter === 'professional_payout' ? 'active' : ''}`}
        onClick={() => setPaymentFilter('professional_payout')}>
        <FaArrowUp /> Professional Payouts
      </button>
    </div>

    {/* Table */}
    <div className="excel-table-wrap">
      <table className="excel-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Party</th>
            <th>Related To</th>
            <th>Method</th>
            <th>Amount (RWF)</th>
            <th>Reference</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments
            .filter(p => paymentFilter === 'all' || p.payment_type === paymentFilter)
            .map(p => (
              <tr key={p.id}>
                <td>{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '-'}</td>
                <td>
                  {p.payment_type === 'client_payment' ? (
                    <span className="payment-badge payment-badge-in">
                      <FaArrowDown /> Client Paid
                    </span>
                  ) : (
                    <span className="payment-badge payment-badge-out">
                      <FaArrowUp /> Paid Professional
                    </span>
                  )}
                </td>
                <td><b>{p.party_name || '-'}</b></td>
                <td>
                  {p.request_number && <div>📋 {p.request_number}</div>}
                  {p.contract_type && <div>📄 {p.contract_type} — {p.contract_party}</div>}
                  {!p.request_number && !p.contract_type && <span>-</span>}
                </td>
                <td style={{ textTransform: 'capitalize' }}>
                  {(p.method || '').replace('_', ' ')}
                </td>
                <td style={{
                  fontWeight: 700,
                  color: p.payment_type === 'client_payment' ? '#0a5c36' : '#c2620f'
                }}>
                  {p.payment_type === 'client_payment' ? '+' : '−'}{fmtMoney(p.amount)}
                </td>
                <td>{p.reference || '-'}</td>
                <td style={{ maxWidth: 200, whiteSpace: 'normal', fontSize: 12 }}>
                  {p.notes || '-'}
                </td>
                <td>
                  <div className="excel-actions">
                    <button className="excel-btn excel-edit" onClick={() => editPayment(p)} title="Edit">
                      <FaEdit />
                    </button>
                    <button className="excel-btn excel-delete" onClick={() => removePayment(p)} title="Delete">
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          {payments.filter(p => paymentFilter === 'all' || p.payment_type === paymentFilter).length === 0 && (
            <tr>
              <td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                No payments recorded yet. Click "Record Client Payment" or "Pay a Professional" above.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
)}

        {/* ================= REQUESTS ================= */}
        {section === 'requests' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head"><h3>All Requests</h3></div>
            <div className="requests-info-banner">
              <FaInfoCircle /> Changing a request status will automatically send a well-designed email to the client.
              Rejecting requires a reason, which is also sent to the client.
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>#</th><th>Client</th><th>Email</th><th>Service</th>
                    <th>Event</th><th>Guests</th><th>Staff</th>
                    <th>Date</th><th>Location</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map(r => (
                    <tr key={r.id}>
                      <td>{r.request_number}</td>
                      <td>{renderEditableCell(r, 'full_name')}</td>
                      <td>{renderEditableCell(r, 'user_email')}</td>
                      <td>{renderEditableCell(r, 'service_type', 'select', ['Chef', 'Housekeeper', 'Receptionist', 'Cleaner'])}</td>
                      <td>{renderEditableCell(r, 'event_type')}</td>
                      <td>{renderEditableCell(r, 'guests', 'number')}</td>
                      <td>{renderEditableCell(r, 'number_of_professionals', 'number')}</td>
                      <td>{r.event_date ? new Date(r.event_date).toLocaleDateString() : '-'}</td>
                      <td>{renderEditableCell(r, 'location')}</td>
                      <td>{renderStatusCell(r)}</td>
                      <td>{renderActions('requests', r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= PROFESSIONALS ================= */}
        {section === 'professionals' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Professionals</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Professional</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Image</th><th>Name</th><th>Role</th><th>Category</th><th>Specialty</th>
                    <th>Experience</th><th>Rate ($/hr)</th><th>Featured</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.professionals.map(c => (
                    <tr key={c.id} className={c.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(c)}</td>
                      <td>{renderEditableCell(c, 'name')}</td>
                      <td>{renderEditableCell(c, 'role', 'select', ['Chef', 'Housekeeper', 'Receptionist', 'Cleaner'])}</td>
                      <td>{renderEditableCell(c, 'category')}</td>
                      <td>{renderEditableCell(c, 'specialty')}</td>
                      <td>{renderEditableCell(c, 'experience')}</td>
                      <td>{renderEditableCell(c, 'hourly_rate', 'number')}</td>
                      <td>{renderEditableCell(c, 'featured', 'select', ['0', '1'])}</td>
                      <td>{renderActiveCell('professionals', c)}</td>
                      <td>{renderActions('professionals', c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= USERS ================= */}
        {section === 'users' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Users</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add User</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Phone</th><th>Address</th><th>Joined</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {data.users.map(u => (
                    <tr key={u.id}>
                      <td>{renderEditableCell(u, 'full_name')}</td>
                      <td>{renderEditableCell(u, 'email')}</td>
                      <td>{renderEditableCell(u, 'phone')}</td>
                      <td>{renderEditableCell(u, 'address')}</td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td>{renderActions('users', u)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* ================= SERVICES ================= */}
{section === 'services' && (
  <div className="dashboard-section">
    <div className="dashboard-section-head">
      <h3>Services</h3>
      <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Service</button>
    </div>
    <div className="excel-table-wrap">
      <table className="excel-table">
        <thead>
          <tr>
            <th>Image</th><th>Title</th><th>Subtitle</th>
            <th>Short Desc</th><th>Price From</th><th>Duration</th>
            <th>Icon</th><th>Order</th><th>Active</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.services.map(s => (
            <tr key={s.id} className={s.is_active ? '' : 'row-inactive'}>
              <td>{renderImageCell(s)}</td>
              <td>{renderEditableCell(s, 'title')}</td>
              <td>{renderEditableCell(s, 'subtitle')}</td>
              <td>{renderEditableCell(s, 'description', 'textarea')}</td>
              <td>{renderEditableCell(s, 'price_from')}</td>
              <td>{renderEditableCell(s, 'duration_info')}</td>
              <td>{renderEditableCell(s, 'icon', 'select', ['chef', 'home', 'reception', 'clean'])}</td>
              <td>{renderEditableCell(s, 'sort_order', 'number')}</td>
              <td>{renderActiveCell('services', s)}</td>
              <td>{renderActions('services', s)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Extended editable section for the currently-selected row (long fields) */}
    <p className="services-edit-hint">
      💡 Click <b>Edit</b> on any row to update its <b>long description</b>, <b>features</b>, and <b>CTA label</b> in the modal below.
    </p>
    {editingRow && data.services.find(s => s.id === editingRow) && (
      <div className="services-extended-editor">
        <h4>Extended fields for: {editingData.title}</h4>
        <div className="services-extended-grid">
          <label>
            <span>Long Description</span>
            <textarea rows="4"
              value={editingData.long_description || ''}
              onChange={e => setEditingData({ ...editingData, long_description: e.target.value })} />
          </label>
          <label>
            <span>Features (separate with <b>|</b>)</span>
            <textarea rows="4"
              placeholder="Custom menu design|Fresh ingredients|On-site cooking"
              value={editingData.features || ''}
              onChange={e => setEditingData({ ...editingData, features: e.target.value })} />
          </label>
          <label>
            <span>CTA Button Label</span>
            <input type="text"
              placeholder="Request This Service"
              value={editingData.cta_label || ''}
              onChange={e => setEditingData({ ...editingData, cta_label: e.target.value })} />
          </label>
        </div>
      </div>
    )}
  </div>
)}

        {/* ================= GALLERY ================= */}
        {section === 'gallery' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Gallery</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Image</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Image</th><th>Title</th><th>Description</th>
                    <th>Type</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.gallery.map(g => (
                    <tr key={g.id} className={g.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(g)}</td>
                      <td>{renderEditableCell(g, 'title')}</td>
                      <td>{renderEditableCell(g, 'description', 'textarea')}</td>
                      <td>{renderEditableCell(g, 'type', 'select', ['action', 'recipe'])}</td>
                      <td>{renderActiveCell('gallery', g)}</td>
                      <td>{renderActions('gallery', g)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= RECIPES ================= */}
        {section === 'recipes' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Recipes</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Recipe</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Image</th><th>Title</th><th>Description</th>
                    <th>Rating</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recipes.map(r => (
                    <tr key={r.id} className={r.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(r)}</td>
                      <td>{renderEditableCell(r, 'title')}</td>
                      <td>{renderEditableCell(r, 'description', 'textarea')}</td>
                      <td><FaStar /> {Number(r.avg_rating || 0).toFixed(1)} ({r.rating_count})</td>
                      <td>{renderActiveCell('recipes', r)}</td>
                      <td>{renderActions('recipes', r)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= CONTRACTS ================= */}
        {section === 'contracts' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Contracts</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> New Contract</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Type</th><th>Party</th><th>Email</th><th>Phone</th>
                    <th>Amount</th><th>Start</th><th>End</th><th>Signed</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.contracts.map(c => (
                    <tr key={c.id}>
                      <td>{renderEditableCell(c, 'contract_type')}</td>
                      <td>{renderEditableCell(c, 'party_name')}</td>
                      <td>{renderEditableCell(c, 'party_email')}</td>
                      <td>{renderEditableCell(c, 'party_phone')}</td>
                      <td>{renderEditableCell(c, 'amount', 'number')}</td>
                      <td>{renderEditableCell(c, 'start_date', 'date')}</td>
                      <td>{renderEditableCell(c, 'end_date', 'date')}</td>
                      <td>
                        <button className={`excel-signed ${c.signed ? 'signed' : 'unsigned'}`}
                          onClick={() => signContract(c)}>
                          {c.signed ? <><FaCheck /> Signed</> : 'Unsigned'}
                        </button>
                      </td>
                      <td>
                        <div className="excel-actions">
                          <button className="excel-btn excel-pdf" onClick={() => downloadContractPDF(c)} title="Download PDF"><FaDownload /></button>
                          <button className="excel-btn excel-print" onClick={() => printContractPDF(c)} title="Print PDF"><FaPrint /></button>
                          <button className="excel-btn excel-wa" onClick={() => shareContractWhatsApp(c)} title="Share via WhatsApp"><FaWhatsapp /></button>
                          <button className="excel-btn excel-mail" onClick={() => shareContractEmail(c)} title="Share via Email"><FaEnvelope /></button>
                          {renderActions('contracts', c)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= REPORTS ================= */}
        {section === 'reports' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>System Reports</h3>
            </div>

            <div className="reports-period-bar">
              {[
                { key: 'daily', label: 'Daily', icon: <FaCalendarDay /> },
                { key: 'monthly', label: 'Monthly', icon: <FaCalendarAlt /> },
                { key: 'annually', label: 'Annually', icon: <FaCalendarWeek /> }
              ].map(p => (
                <button key={p.key}
                  className={`reports-period-btn ${reportPeriod === p.key ? 'active' : ''}`}
                  onClick={() => { setReportPeriod(p.key); loadReportPreview(p.key); }}>
                  {p.icon} {p.label}
                </button>
              ))}
              <button className="reports-generate-btn"
                onClick={() => generateReportPDF(reportPeriod, 'download')}
                disabled={reportLoading}>
                <FaFilePdf /> {reportLoading ? 'Generating…' : `Generate & Save ${reportPeriod} Report`}
              </button>
              <button className="reports-print-btn"
                onClick={() => generateReportPDF(reportPeriod, 'print')}
                disabled={reportLoading}>
                <FaPrint /> Print
              </button>
            </div>

            {reportPreview ? (
              <div className="reports-preview">
                <h4>Preview — {reportPreview.period}</h4>
                <p className="reports-range">
                  From <b>{new Date(reportPreview.rangeStart).toLocaleString()}</b> to <b>{new Date(reportPreview.rangeEnd).toLocaleString()}</b>
                </p>

                <div className="reports-summary-grid">
                  {[
                    ['Requests', reportPreview.data.requests?.[0]?.c ?? 0],
                    ['Users', reportPreview.data.users?.[0]?.c ?? 0],
                    ['Professionals', reportPreview.data.professionals?.[0]?.c ?? 0],
                    ['Gallery', reportPreview.data.gallery?.[0]?.c ?? 0],
                    ['Recipes', reportPreview.data.recipes?.[0]?.c ?? 0],
                    ['Contracts', reportPreview.data.contracts?.[0]?.c ?? 0],
                    ['Revenue (RWF)', reportPreview.data.contractsRevenue?.[0]?.c ?? 0],
                    ['Messages', reportPreview.data.messages?.[0]?.c ?? 0]
                  ].map(([label, value], i) => (
                    <div key={i} className="reports-summary-card">
                      <h3>{value}</h3>
                      <p>{label}</p>
                    </div>
                  ))}
                </div>

                {reportPreview.data.requestsByStatus?.length > 0 && (
                  <div className="reports-block">
                    <h5>Requests by Status</h5>
                    <ul>
                      {reportPreview.data.requestsByStatus.map((r, i) => (
                        <li key={i}><b>{r.status}:</b> {r.c}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="reports-save-note">
                  💾 Reports are automatically saved to your <b>Documents/{'{Hospify Link Reports}'}</b> folder
                  organized by period (Daily / Monthly / Annual).
                </p>
              </div>
            ) : (
              <p className="reports-empty">Select a period above to preview the report.</p>
            )}
          </div>
        )}

        {/* ================= TEAM ================= */}
        {section === 'team' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Team Members</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Member</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr><th>Image</th><th>Name</th><th>Role</th><th>Bio</th><th>Active</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {data.team.map(m => (
                    <tr key={m.id} className={m.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(m)}</td>
                      <td>{renderEditableCell(m, 'name')}</td>
                      <td>{renderEditableCell(m, 'role')}</td>
                      <td>{renderEditableCell(m, 'bio', 'textarea')}</td>
                      <td>{renderActiveCell('team', m)}</td>
                      <td>{renderActions('team', m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= PARTNERS ================= */}
        {section === 'partners' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Partners</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Partner</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Logo</th><th>Name</th><th>Website URL</th>
                    <th>Sort Order</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.map(p => (
                    <tr key={p.id} className={p.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(p, 'logo')}</td>
                      <td>{renderEditableCell(p, 'name')}</td>
                      <td>{renderEditableCell(p, 'website_url')}</td>
                      <td>{renderEditableCell(p, 'sort_order', 'number')}</td>
                      <td>{renderActiveCell('partners', p)}</td>
                      <td>{renderActions('partners', p)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= TESTIMONIALS ================= */}
        {section === 'testimonials' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Client Testimonials</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Testimonial</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Photo</th><th>Name</th><th>Role</th>
                    <th>Text</th><th>Sort Order</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.testimonials.map(t => (
                    <tr key={t.id} className={t.is_active ? '' : 'row-inactive'}>
                      <td>{renderImageCell(t)}</td>
                      <td>{renderEditableCell(t, 'name')}</td>
                      <td>{renderEditableCell(t, 'role')}</td>
                      <td>{renderEditableCell(t, 'text', 'textarea')}</td>
                      <td>{renderEditableCell(t, 'sort_order', 'number')}</td>
                      <td>{renderActiveCell('testimonials', t)}</td>
                      <td>{renderActions('testimonials', t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= ABOUT PAGE ================= */}
        {section === 'about' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>About Page Content</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add About Item</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Section</th><th>Title</th><th>Subtitle</th>
                    <th>Description</th><th>Icon</th><th>Order</th>
                    <th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.about.map(a => (
                    <tr key={a.id} className={a.is_active ? '' : 'row-inactive'}>
                      <td>
                        {renderEditableCell(a, 'section', 'select', [
                          'hero', 'mission', 'vision', 'service', 'value', 'why'
                        ])}
                      </td>
                      <td>{renderEditableCell(a, 'title')}</td>
                      <td>{renderEditableCell(a, 'subtitle')}</td>
                      <td>{renderEditableCell(a, 'description', 'textarea')}</td>
                      <td>
                        {renderEditableCell(a, 'icon', 'select', [
                          'bullseye', 'eye', 'heart', 'chef', 'home',
                          'reception', 'clean', 'certificate', 'clock',
                          'handshake', 'leaf',
                          'scale', 'trophy', 'fire', 'bulb', 'users', 'bolt', 'star', 'gem',
                          'shield', 'seedling', 'award', 'globe', 'fingerprint', 'compass',
                          'anchor', 'hands', 'ribbon', 'smile', 'book', 'palette'
                        ])}
                      </td>
                      <td>{renderEditableCell(a, 'sort_order', 'number')}</td>
                      <td>{renderActiveCell('about', a)}</td>
                      <td>{renderActions('about', a)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= WHY CHOOSE US ================= */}
        {section === 'whychoose' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Why Choose Us</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Item</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr><th>Icon</th><th>Title</th><th>Description</th><th>Active</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {data.whychoose.map(w => (
                    <tr key={w.id} className={w.is_active ? '' : 'row-inactive'}>
                      <td>{renderEditableCell(w, 'icon', 'select', ['certificate', 'clock', 'handshake', 'leaf'])}</td>
                      <td>{renderEditableCell(w, 'title')}</td>
                      <td>{renderEditableCell(w, 'description', 'textarea')}</td>
                      <td>{renderActiveCell('whychoose', w)}</td>
                      <td>{renderActions('whychoose', w)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= STATS ================= */}
        {section === 'stats' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Stats</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Stat</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Label</th><th>Value</th><th>Suffix</th>
                    <th>Icon</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stats.map(s => (
                    <tr key={s.id} className={s.is_active ? '' : 'row-inactive'}>
                      <td>{renderEditableCell(s, 'label')}</td>
                      <td>{renderEditableCell(s, 'value', 'number')}</td>
                      <td>{renderEditableCell(s, 'suffix')}</td>
                      <td>{renderEditableCell(s, 'icon', 'select', ['chef', 'smile', 'calendar', 'award', 'users'])}</td>
                      <td>{renderActiveCell('stats', s)}</td>
                      <td>{renderActions('stats', s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= FOOTER SETTINGS ================= */}
        {section === 'footer' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Footer Settings</h3>
              <button className="lca-btn-primary" onClick={saveFooterSettings} disabled={loading}>
                <FaSave /> {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            <div className="footer-settings-grid">
              {[
                { key: 'brand_name', label: 'Brand Name' },
                { key: 'description', label: 'Description', type: 'textarea' },
                { key: 'address', label: 'Address' },
                { key: 'phone', label: 'Phone' },
                { key: 'email', label: 'Email' },
                { key: 'copyright_text', label: 'Copyright Text' },
                { key: 'facebook_url', label: 'Facebook URL' },
                { key: 'twitter_url', label: 'Twitter URL' },
                { key: 'instagram_url', label: 'Instagram URL' },
                { key: 'linkedin_url', label: 'LinkedIn URL' },
                { key: 'youtube_url', label: 'YouTube URL' },
                { key: 'tiktok_url', label: 'TikTok URL' },
                { key: 'whatsapp_number', label: 'WhatsApp Number (e.g. 250789951922)' },
                { key: 'whatsapp_message', label: 'WhatsApp Pre-filled Message', type: 'textarea' }
              ].map(f => (
                <div key={f.key} className="footer-settings-field">
                  <label>{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea rows="3"
                      value={footerSettings[f.key] || ''}
                      onChange={e => setFooterSettings({ ...footerSettings, [f.key]: e.target.value })} />
                  ) : (
                    <input type="text"
                      value={footerSettings[f.key] || ''}
                      onChange={e => setFooterSettings({ ...footerSettings, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ================= FOOTER LINKS ================= */}
        {section === 'footerlinks' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Footer Links</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Link</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Column</th><th>Label</th><th>URL</th>
                    <th>Sort Order</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.footerLinks.map(l => (
                    <tr key={l.id} className={l.is_active ? '' : 'row-inactive'}>
                      <td>{renderEditableCell(l, 'column_name', 'select', ['Quick Links', 'Services'])}</td>
                      <td>{renderEditableCell(l, 'label')}</td>
                      <td>{renderEditableCell(l, 'url')}</td>
                      <td>{renderEditableCell(l, 'sort_order', 'number')}</td>
                      <td>{renderActiveCell('footerlinks', l)}</td>
                      <td>{renderActions('footerlinks', l)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= CHATBOT Q&A ================= */}
        {section === 'chatbot' && (
          <div className="dashboard-section">
            <div className="dashboard-section-head">
              <h3>Chatbot Quick Q&A</h3>
              <button className="lca-btn-primary" onClick={openAdd}><FaPlus /> Add Q&A</button>
            </div>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Question</th><th>Answer</th>
                    <th>Sort Order</th><th>Active</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.chatbot.map(q => (
                    <tr key={q.id} className={q.is_active ? '' : 'row-inactive'}>
                      <td>{renderEditableCell(q, 'question')}</td>
                      <td>{renderEditableCell(q, 'answer', 'textarea')}</td>
                      <td>{renderEditableCell(q, 'sort_order', 'number')}</td>
                      <td>{renderActiveCell('chatbot', q)}</td>
                      <td>{renderActions('chatbot', q)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ================= MESSAGES ================= */}
        {section === 'messages' && (
          <div className="dashboard-section">
            <h3>Contact Messages</h3>
            <div className="excel-table-wrap">
              <table className="excel-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Email</th><th>Subject</th>
                    <th>Message</th><th>Date</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.messages.map(m => (
                    <tr key={m.id}>
                      <td>{renderEditableCell(m, 'name')}</td>
                      <td>{renderEditableCell(m, 'email')}</td>
                      <td>{renderEditableCell(m, 'subject')}</td>
                      <td>{renderEditableCell(m, 'message', 'textarea')}</td>
                      <td>{new Date(m.created_at).toLocaleDateString()}</td>
                      <td>{renderActions('messages', m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* ================= ADD MODALS ================= */}
      {showAddModal && section === 'hero' && renderAddModal('hero', [
        { name: 'title', placeholder: 'Slide Title' },
        { name: 'subtitle', placeholder: 'Slide Subtitle' },
        { name: 'primary_btn_label', placeholder: 'Primary Button Label (e.g. Request a Chef)' },
        { name: 'primary_btn_action', type: 'select', placeholder: 'Primary Action', options: ['request', '/chefs', '/gallery', '/contact'] },
        { name: 'secondary_btn_label', placeholder: 'Secondary Button Label (e.g. Browse Chefs)' },
        { name: 'secondary_btn_link', placeholder: 'Secondary Link (e.g. /chefs or /chefs?role=Chef)' },
        { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
      ])}
      {showAddModal && section === 'professionals' && renderAddModal('professionals', [
        { name: 'name', placeholder: 'Full Name' },
        { name: 'role', type: 'select', placeholder: 'Role', options: ['Chef', 'Housekeeper', 'Receptionist', 'Cleaner'] },
        { name: 'category', placeholder: 'Category (e.g. Italian, Residential)' },
        { name: 'specialty', placeholder: 'Specialty' },
        { name: 'experience', placeholder: 'Experience (e.g. 10 years)' },
        { name: 'hourly_rate', type: 'number', placeholder: 'Hourly Rate ($)' },
        { name: 'cv_url', placeholder: 'CV URL' },
        { name: 'bio', type: 'textarea', placeholder: 'Bio' },
        { name: 'featured', type: 'select', placeholder: 'Featured', options: ['0', '1'] }
      ])}
      {showAddModal && section === 'users' && renderAddModal('users', [
        { name: 'full_name', placeholder: 'Full Name' },
        { name: 'email', placeholder: 'Email' },
        { name: 'phone', placeholder: 'Phone' },
        { name: 'address', placeholder: 'Address' }
      ])}
      {showAddModal && section === 'services' && renderAddModal('services', [
  { name: 'title', placeholder: 'Service Title (e.g. Chef Services)' },
  { name: 'subtitle', placeholder: 'Subtitle (e.g. Personal chefs for every occasion)' },
  { name: 'description', type: 'textarea', placeholder: 'Short description (shown on card)' },
  { name: 'long_description', type: 'textarea', placeholder: 'Long description (shown in modal)' },
  { name: 'features', type: 'textarea', placeholder: 'Features, separated by | (e.g. Custom menus|Fresh ingredients|Cleanup)' },
  { name: 'price_from', placeholder: 'Price from (e.g. From $35/hour)' },
  { name: 'duration_info', placeholder: 'Duration info (e.g. Minimum 3 hours)' },
  { name: 'cta_label', placeholder: 'CTA button label (e.g. Request a Chef)' },
  { name: 'icon', type: 'select', placeholder: 'Icon', options: ['chef', 'home', 'reception', 'clean'] },
  { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
])}
      {showAddModal && section === 'gallery' && renderAddModal('gallery', [
        { name: 'title', placeholder: 'Title' },
        { name: 'description', type: 'textarea', placeholder: 'Description' },
        { name: 'type', type: 'select', placeholder: 'Type', options: ['action', 'recipe'] }
      ])}
      {showAddModal && section === 'recipes' && renderAddModal('recipes', [
        { name: 'title', placeholder: 'Recipe Title' },
        { name: 'description', type: 'textarea', placeholder: 'Description' }
      ])}
      {showAddModal && section === 'contracts' && renderAddModal('contracts', [
        { name: 'contract_type', type: 'select', placeholder: 'Type', options: ['Chef', 'Client'] },
        { name: 'party_name', placeholder: 'Party Name' },
        { name: 'party_email', placeholder: 'Party Email' },
        { name: 'party_phone', placeholder: 'Party Phone' },
        { name: 'amount', type: 'number', placeholder: 'Amount (RWF)' },
        { name: 'start_date', type: 'date', placeholder: 'Start Date' },
        { name: 'end_date', type: 'date', placeholder: 'End Date' },
        { name: 'terms', type: 'textarea', placeholder: 'Contract Terms' }
      ])}
      {showAddModal && section === 'team' && renderAddModal('team', [
        { name: 'name', placeholder: 'Name' },
        { name: 'role', placeholder: 'Role' },
        { name: 'bio', type: 'textarea', placeholder: 'Bio' }
      ])}
      {showAddModal && section === 'partners' && renderAddModal('partners', [
        { name: 'name', placeholder: 'Partner Name' },
        { name: 'website_url', placeholder: 'Website URL (https://...)' },
        { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
      ])}
      {showAddModal && section === 'testimonials' && renderAddModal('testimonials', [
        { name: 'name', placeholder: 'Client Name' },
        { name: 'role', placeholder: 'Role (e.g. Wedding Client)' },
        { name: 'text', type: 'textarea', placeholder: 'Testimonial Text' },
        { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
      ])}
      {showAddModal && section === 'about' && renderAddModal('about', [
        { name: 'section', type: 'select', placeholder: 'Section', options: ['hero', 'mission', 'vision', 'service', 'value', 'why'] },
        { name: 'title', placeholder: 'Title' },
        { name: 'subtitle', placeholder: 'Subtitle (only for hero)' },
        { name: 'description', type: 'textarea', placeholder: 'Description' },
        { name: 'icon', type: 'select', placeholder: 'Icon', options: [
          'bullseye', 'eye', 'heart', 'chef', 'home', 'reception', 'clean',
          'certificate', 'clock', 'handshake', 'leaf',
          'scale', 'trophy', 'fire', 'bulb', 'users', 'bolt', 'star', 'gem',
          'shield', 'seedling', 'award', 'globe', 'fingerprint', 'compass',
          'anchor', 'hands', 'ribbon', 'smile', 'book', 'palette'
        ] },
        { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
      ])}
      {showAddModal && section === 'whychoose' && renderAddModal('whychoose', [
        { name: 'title', placeholder: 'Title' },
        { name: 'description', type: 'textarea', placeholder: 'Description' },
        { name: 'icon', type: 'select', placeholder: 'Icon', options: ['certificate', 'clock', 'handshake', 'leaf'] }
      ])}
      {showAddModal && section === 'stats' && renderAddModal('stats', [
        { name: 'label', placeholder: 'Label' },
        { name: 'value', type: 'number', placeholder: 'Value' },
        { name: 'suffix', placeholder: 'Suffix (e.g. +)' },
        { name: 'icon', type: 'select', placeholder: 'Icon', options: ['chef', 'smile', 'calendar', 'award', 'users'] }
      ])}
      {showAddModal && section === 'footerlinks' && renderAddModal('footer/links', [
        { name: 'column_name', type: 'select', placeholder: 'Column', options: ['Quick Links', 'Services'] },
        { name: 'label', placeholder: 'Label (e.g. Home)' },
        { name: 'url', placeholder: 'URL (e.g. /about)' },
        { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
      ])}
      {showAddModal && section === 'chatbot' && renderAddModal('chatbot/qa', [
        { name: 'question', placeholder: 'Question' },
        { name: 'answer', type: 'textarea', placeholder: 'Answer' },
        { name: 'sort_order', type: 'number', placeholder: 'Sort Order' }
      ])}

{showAddModal && section === 'updates' && renderAddModal('updates', [
  { name: 'title', placeholder: 'Update Title' },
  { name: 'description', type: 'textarea', placeholder: 'Short description (shown on card)' },
  { name: 'long_description', type: 'textarea', placeholder: 'Long description (shown in modal)' },
  { name: 'type', type: 'select', placeholder: 'Type', options: ['latest', 'upcoming'] }
])}

{/* ================= PAYMENT MODAL ================= */}
{showPaymentModal && (
  <div className="dashboard-editor-overlay" onClick={() => setShowPaymentModal(false)}>
    <div className="dashboard-editor payment-editor" onClick={e => e.stopPropagation()}>
      <h3 style={{ marginBottom: 16 }}>
        {editingPaymentId
          ? 'Edit Payment'
          : paymentMode === 'client'
            ? '💰 Record Client Payment'
            : '💸 Pay a Professional'}
      </h3>

      <div className="payment-modal-fields">
        <label>
          <span>Related Request (optional)</span>
          <select
            value={paymentData.request_id}
            onChange={e => {
              const id = e.target.value;
              const req = data.requests.find(r => String(r.id) === String(id));
              setPaymentData({
                ...paymentData,
                request_id: id,
                party_name: paymentMode === 'client' && req?.full_name
                  ? req.full_name
                  : paymentData.party_name
              });
            }}>
            <option value="">— None —</option>
            {data.requests.map(r => (
              <option key={r.id} value={r.id}>
                {r.request_number} — {r.full_name} ({r.service_type})
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Related Contract (optional)</span>
          <select
            value={paymentData.contract_id}
            onChange={e => {
              const id = e.target.value;
              const ct = data.contracts.find(c => String(c.id) === String(id));
              setPaymentData({
                ...paymentData,
                contract_id: id,
                party_name: paymentMode === 'client' && ct?.party_name
                  ? ct.party_name
                  : paymentData.party_name
              });
            }}>
            <option value="">— None —</option>
            {data.contracts.map(c => (
              <option key={c.id} value={c.id}>
                #{c.id} — {c.contract_type} — {c.party_name}
              </option>
            ))}
          </select>
        </label>

        {paymentMode === 'professional' ? (
          <label>
            <span>Professional *</span>
            <select
              value={paymentData.party_id}
              onChange={e => {
                const id = e.target.value;
                const prof = professionalsWithEarnings.find(p => String(p.id) === String(id));
                setPaymentData({
                  ...paymentData,
                  party_id: id,
                  party_name: prof?.name || ''
                });
              }}>
              <option value="">— Select Professional —</option>
              {professionalsWithEarnings.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.role} (earned: {fmtMoney(p.total_earned)} RWF)
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label>
            <span>Client Name *</span>
            <input
              type="text"
              placeholder="Client's full name"
              value={paymentData.party_name}
              onChange={e => setPaymentData({ ...paymentData, party_name: e.target.value })}
            />
          </label>
        )}

        <label>
          <span>Amount (RWF) *</span>
          <input
            type="number"
            placeholder="0"
            value={paymentData.amount}
            onChange={e => setPaymentData({ ...paymentData, amount: e.target.value })}
          />
        </label>

        <label>
          <span>Payment Method</span>
          <select
            value={paymentData.method}
            onChange={e => setPaymentData({ ...paymentData, method: e.target.value })}>
            <option value="cash">Cash</option>
            <option value="mobile_money">Mobile Money</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label>
          <span>Date Paid</span>
          <input
            type="date"
            value={paymentData.paid_at}
            onChange={e => setPaymentData({ ...paymentData, paid_at: e.target.value })}
          />
        </label>

        <label>
          <span>Reference / Receipt #</span>
          <input
            type="text"
            placeholder="e.g. MOMO-123456"
            value={paymentData.reference}
            onChange={e => setPaymentData({ ...paymentData, reference: e.target.value })}
          />
        </label>

        <label className="payment-modal-full">
          <span>Notes</span>
          <textarea
            rows="3"
            placeholder="Any extra information about this payment…"
            value={paymentData.notes}
            onChange={e => setPaymentData({ ...paymentData, notes: e.target.value })}
          />
        </label>
      </div>

      <div className="payment-impact-preview">
        This payment will {paymentMode === 'client' ? 'add' : 'subtract'}{' '}
        <b style={{ color: paymentMode === 'client' ? '#0a5c36' : '#c2620f' }}>
          {fmtMoney(paymentData.amount)} RWF
        </b>{' '}
        {paymentMode === 'client' ? 'to' : 'from'} your net earnings.
      </div>

      <div className="dashboard-editor-actions">
        <button className="lca-btn-primary" disabled={loading} onClick={savePayment}>
          {loading ? 'Saving…' : (editingPaymentId ? 'Update Payment' : 'Save Payment')}
        </button>
        <button className="dashboard-editor-cancel" onClick={() => setShowPaymentModal(false)}>
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
      {/* ================= REJECTION MODAL ================= */}
      {showRejectModal && (
        <div className="dashboard-editor-overlay" onClick={() => { if (!rejectLoading) setShowRejectModal(false); }}>
          <div className="dashboard-editor reject-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#d0021b', marginBottom: 8 }}>
              <FaTimes /> Reject Request
            </h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16, lineHeight: 1.6 }}>
              You are about to reject request <b>{rejectRequest?.request_number}</b> from{' '}
              <b>{rejectRequest?.full_name}</b> ({rejectRequest?.user_email}).
              <br />
              Please provide a clear reason — it will be sent to the client via email.
            </p>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 6 }}>
              Reason for Rejection *
            </label>
            <textarea
              rows="5"
              value={rejectionReason}
              onChange={e => setRejectionReason(e.target.value)}
              placeholder="e.g. Unfortunately, we do not have available professionals for your selected date. Please try another date or contact us for alternatives."
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '2px solid #e0e0e0',
                borderRadius: 8,
                fontSize: 13,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              autoFocus
            />
            <div className="dashboard-editor-actions" style={{ marginTop: 16 }}>
              <button
                className="lca-btn-primary"
                style={{ background: 'linear-gradient(135deg,#d0021b,#a00d20)' }}
                disabled={rejectLoading || !rejectionReason.trim()}
                onClick={submitRejection}
              >
                {rejectLoading ? 'Sending…' : '❌ Reject & Notify Client'}
              </button>
              <button
                className="dashboard-editor-cancel"
                onClick={() => setShowRejectModal(false)}
                disabled={rejectLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManagerDashboard;
